/**
 * Discovery Quiz Data
 *
 * Questions, preference mapping, recommendation engine, and Typeform export.
 */

import {
  QuizQuestion,
  QuizAnswers,
  QuizRecommendation,
  TypeformQuestion,
  EnhancedQuizRecommendation,
  ProfileSummary,
  EnhancedQuizResult,
  QuizStepId,
} from './quizTypes';

// ---------------------------------------------------------------------------
// Questions
// ---------------------------------------------------------------------------

export const QUIZ_QUESTIONS: QuizQuestion[] = [
  {
    id: 'persona',
    title: 'What best describes you?',
    subtitle: 'This helps us tailor your experience.',
    choices: [
      {
        id: 'builder',
        label: "I'm building my first portfolio",
        description: 'I have a budget and want help getting started.',
      },
      {
        id: 'manager',
        label: 'I actively manage my investments',
        description: 'I want ongoing risk and performance insights.',
      },
      {
        id: 'researcher',
        label: 'I research stocks before I trade',
        description: 'I need fast, reliable analysis before making moves.',
      },
    ],
  },
  {
    id: 'goal',
    title: "What's your primary investment goal?",
    choices: [
      { id: 'growth', label: 'Growth', description: 'Maximize long-term capital appreciation.' },
      { id: 'income', label: 'Income', description: 'Generate steady dividend or yield income.' },
      { id: 'preservation', label: 'Preservation', description: 'Protect capital and minimize losses.' },
      { id: 'balanced', label: 'Balanced', description: 'A mix of growth and income.' },
    ],
  },
  {
    id: 'risk',
    title: 'How do you feel about risk?',
    subtitle: 'There are no wrong answers — this helps us calibrate recommendations.',
    choices: [
      { id: 'conservative', label: 'Conservative', description: 'I prefer stability over high returns.' },
      { id: 'moderate', label: 'Moderate', description: 'I can handle some ups and downs.' },
      { id: 'aggressive', label: 'Aggressive', description: "I'm comfortable with significant swings for higher potential." },
    ],
  },
  {
    id: 'horizon',
    title: "What's your investment timeline?",
    choices: [
      { id: 'short', label: 'Less than 1 year', description: 'Short-term trades or near-term goals.' },
      { id: 'medium', label: '1 – 5 years', description: 'Medium-term growth or milestones.' },
      { id: 'long', label: '5+ years', description: 'Long-term wealth building.' },
    ],
  },
  {
    id: 'needs',
    title: 'What would help you most?',
    subtitle: 'Select all that apply.',
    multiSelect: true,
    choices: [
      { id: 'undervalued', label: 'Find undervalued stocks', description: 'AI scoring and fundamental analysis.' },
      { id: 'portfolio_risk', label: 'Assess portfolio risk', description: 'Risk scoring and stress testing.' },
      { id: 'dividends', label: 'Analyze dividend quality', description: 'Dividend safety scores and sustainability metrics.' },
      { id: 'unusual_activity', label: 'Spot unusual activity', description: 'Options flow and volume intelligence.' },
      { id: 'ai_reports', label: 'AI research reports', description: 'On-demand analysis powered by our AI agents.' },
    ],
  },
];

// ---------------------------------------------------------------------------
// Preference mapping – converts quiz answers to Firestore-compatible shape
// ---------------------------------------------------------------------------

export function mapAnswersToPreferences(answers: QuizAnswers) {
  const needsToAgents: Record<string, string> = {
    undervalued: 'jerry',
    portfolio_risk: 'bradford',
    dividends: 'kelly',
    unusual_activity: 'charles',
    ai_reports: 'chat',
  };

  const interestedAgents = (answers.needs ?? [])
    .map((n) => needsToAgents[n])
    .filter(Boolean);

  return {
    preferences: {
      investmentGoal: answers.goal as 'growth' | 'income' | 'preservation' | 'balanced' | undefined,
      riskTolerance: answers.risk as 'conservative' | 'moderate' | 'aggressive' | undefined,
      investmentHorizon: answers.horizon as 'short' | 'medium' | 'long' | undefined,
    },
    onboardingSurvey: {
      completed: true,
      completedAt: new Date(),
      investorType: answers.persona,
      primaryGoal: answers.goal,
      interestedAgents,
    },
  };
}

// ---------------------------------------------------------------------------
// Recommendation engine
// ---------------------------------------------------------------------------

interface PersonaRec {
  headline: string;
  primary: QuizRecommendation;
  secondary: QuizRecommendation;
}

const PERSONA_RECOMMENDATIONS: Record<string, PersonaRec> = {
  builder: {
    headline: "Here's your portfolio building plan",
    primary: {
      title: 'Stock Research',
      description: 'Run institutional-grade analysis on any stock — fundamentals, valuation, dividend quality, and risk-adjusted comparables.',
      route: '/stock-research',
    },
    secondary: {
      title: 'AI Assistant',
      description: 'Ask our AI to help you build a diversified portfolio from scratch.',
      route: '/chat',
    },
  },
  manager: {
    headline: "Here's your risk management suite",
    primary: {
      title: 'Risk Assessment',
      description: 'Upload your holdings and get VaR, stress testing, and diversification analysis.',
      route: '/portfolio/risk-assessment',
    },
    secondary: {
      title: 'Stock Research',
      description: 'Deep-dive into any stock with fundamentals, valuation, and AI insights.',
      route: '/stock-research',
    },
  },
  researcher: {
    headline: "Here's your research toolkit",
    primary: {
      title: 'Stock Research',
      description: 'Deep-dive into any stock with fundamentals, technicals, and AI insights.',
      route: '/stock-research',
    },
    secondary: {
      title: 'AI Assistant',
      description: 'Generate on-demand research reports with charts, tables, and analysis.',
      route: '/chat',
    },
  },
};

const NEEDS_RECOMMENDATIONS: Record<string, QuizRecommendation> = {
  undervalued: {
    title: 'Stock Research',
    description: 'Search any stock for AI scoring and fundamental analysis.',
    route: '/stock-research',
  },
  portfolio_risk: {
    title: 'Risk Assessment',
    description: 'Stress-test your portfolio against real market scenarios.',
    route: '/portfolio/risk-assessment',
  },
  dividends: {
    title: 'Stock Research',
    description: 'Explore dividend quality scores and sustainability metrics.',
    route: '/stock-research',
  },
  unusual_activity: {
    title: 'AI Assistant',
    description: 'Ask about volume signals, unusual activity, and market trends.',
    route: '/chat',
  },
  ai_reports: {
    title: 'AI Assistant',
    description: 'Generate on-demand reports with charts and tables.',
    route: '/chat',
  },
};

export function getRecommendations(answers: QuizAnswers): {
  headline: string;
  recommendations: QuizRecommendation[];
} {
  const persona = answers.persona ?? 'builder';
  const personaRec = PERSONA_RECOMMENDATIONS[persona] ?? PERSONA_RECOMMENDATIONS.builder;

  // Start with persona-based primary + secondary
  const seen = new Set<string>();
  const recs: QuizRecommendation[] = [];

  const add = (rec: QuizRecommendation) => {
    if (!seen.has(rec.route)) {
      seen.add(rec.route);
      recs.push(rec);
    }
  };

  add(personaRec.primary);
  add(personaRec.secondary);

  // Append needs-based recs (up to 3 total)
  for (const need of answers.needs ?? []) {
    if (recs.length >= 3) break;
    const rec = NEEDS_RECOMMENDATIONS[need];
    if (rec) add(rec);
  }

  return { headline: personaRec.headline, recommendations: recs };
}

// ---------------------------------------------------------------------------
// Enhanced recommendation engine — profile summary + personalized benefits
// ---------------------------------------------------------------------------

const ANSWER_LABELS: Record<QuizStepId, Record<string, string>> = {
  persona: {
    builder: 'Portfolio Builder',
    manager: 'Active Manager',
    researcher: 'Stock Researcher',
  },
  goal: {
    growth: 'Growth',
    income: 'Income',
    preservation: 'Preservation',
    balanced: 'Balanced',
  },
  risk: {
    conservative: 'Conservative',
    moderate: 'Moderate',
    aggressive: 'Aggressive',
  },
  horizon: {
    short: 'Less than 1 year',
    medium: '1 - 5 years',
    long: '5+ years',
  },
  needs: {
    undervalued: 'Find undervalued stocks',
    portfolio_risk: 'Assess portfolio risk',
    dividends: 'Analyze dividend quality',
    unusual_activity: 'Spot unusual activity',
    ai_reports: 'AI research reports',
  },
};

export function buildProfileSummary(answers: QuizAnswers): ProfileSummary {
  const label = (step: QuizStepId, id: string | undefined, fallback: string) =>
    ANSWER_LABELS[step][id ?? ''] ?? fallback;

  return {
    persona: { id: answers.persona ?? 'builder', label: label('persona', answers.persona, 'Portfolio Builder') },
    goal: { id: answers.goal ?? 'balanced', label: label('goal', answers.goal, 'Balanced') },
    risk: { id: answers.risk ?? 'moderate', label: label('risk', answers.risk, 'Moderate') },
    horizon: { id: answers.horizon ?? 'medium', label: label('horizon', answers.horizon, '1 - 5 years') },
    needs: (answers.needs ?? []).map((n) => ({ id: n, label: ANSWER_LABELS.needs[n] ?? n })),
  };
}

interface EnhancedPersonaRec {
  headline: string;
  primary: Omit<EnhancedQuizRecommendation, 'step'>;
  secondary: Omit<EnhancedQuizRecommendation, 'step'>;
}

const ENHANCED_PERSONA_RECOMMENDATIONS: Record<string, EnhancedPersonaRec> = {
  builder: {
    headline: "Here's your portfolio building plan",
    primary: {
      title: 'Stock Research',
      description: 'Run institutional-grade analysis on any stock — fundamentals, valuation, dividend quality, and risk-adjusted comparables.',
      personalizedBenefit: 'Start here to learn by exploring real stocks and building conviction before you invest.',
      route: '/stock-research',
    },
    secondary: {
      title: 'AI Assistant',
      description: 'Ask our AI to help you build a diversified portfolio from scratch.',
      personalizedBenefit: 'Get personalized guidance on building your first portfolio step by step.',
      route: '/chat',
    },
  },
  manager: {
    headline: "Here's your risk management suite",
    primary: {
      title: 'Risk Assessment',
      description: 'Upload your holdings for VaR, stress testing, and factor exposure analysis.',
      personalizedBenefit: 'Get a comprehensive risk report to identify hidden vulnerabilities in your portfolio.',
      route: '/portfolio/risk-assessment',
    },
    secondary: {
      title: 'Stock Research',
      description: 'Deep-dive into any holding with fundamentals, valuation, and AI insights.',
      personalizedBenefit: 'Research individual positions in your portfolio for deeper conviction.',
      route: '/stock-research',
    },
  },
  researcher: {
    headline: "Here's your research toolkit",
    primary: {
      title: 'Stock Research',
      description: 'Deep-dive into any stock with fundamentals, technicals, and AI insights.',
      personalizedBenefit: 'Your primary research workbench for fast, reliable analysis before making moves.',
      route: '/stock-research',
    },
    secondary: {
      title: 'AI Assistant',
      description: 'Generate on-demand research reports with charts, tables, and analysis.',
      personalizedBenefit: 'Create detailed research reports and get instant answers on any stock or sector.',
      route: '/chat',
    },
  },
};

function getPersonalizedBenefit(need: string, persona: string): string {
  const benefits: Record<string, Record<string, string>> = {
    undervalued: {
      builder: 'Find your first stocks to invest in based on data-driven scoring.',
      manager: 'Identify new positions to add to your portfolio.',
      researcher: 'Screen for mispriced stocks using fundamental-sentiment analysis.',
    },
    portfolio_risk: {
      builder: 'Understand the risk profile of your new portfolio.',
      manager: 'Monitor and stress-test your existing holdings.',
      researcher: 'Analyze risk factors before recommending positions.',
    },
    dividends: {
      builder: 'Find reliable income stocks for your portfolio.',
      manager: 'Assess the sustainability of your dividend income.',
      researcher: 'Score dividend stocks by quality, growth, and payout safety.',
    },
    unusual_activity: {
      builder: 'Get alerts when stocks show unusual trading patterns.',
      manager: 'Monitor your holdings for volume surges and anomalies.',
      researcher: 'Detect institutional activity and accumulation signals.',
    },
    ai_reports: {
      builder: 'Ask the AI to explain any stock or investing concept.',
      manager: 'Generate on-demand reports on your portfolio holdings.',
      researcher: 'Create detailed research reports with charts and data.',
    },
  };
  return benefits[need]?.[persona] ?? NEEDS_RECOMMENDATIONS[need]?.description ?? '';
}

export function getEnhancedRecommendations(answers: QuizAnswers): EnhancedQuizResult {
  const persona = answers.persona ?? 'builder';
  const personaRec = ENHANCED_PERSONA_RECOMMENDATIONS[persona] ?? ENHANCED_PERSONA_RECOMMENDATIONS.builder;
  const profileSummary = buildProfileSummary(answers);

  const seen = new Set<string>();
  const recs: EnhancedQuizRecommendation[] = [];
  let stepCounter = 1;

  const add = (rec: Omit<EnhancedQuizRecommendation, 'step'>) => {
    if (!seen.has(rec.route)) {
      seen.add(rec.route);
      recs.push({ ...rec, step: stepCounter++ });
    }
  };

  add(personaRec.primary);
  add(personaRec.secondary);

  // Append needs-based recs (up to 3 total)
  for (const need of answers.needs ?? []) {
    if (recs.length >= 3) break;
    const needRec = NEEDS_RECOMMENDATIONS[need];
    if (needRec) {
      add({
        ...needRec,
        personalizedBenefit: getPersonalizedBenefit(need, persona),
      });
    }
  }

  return { headline: personaRec.headline, profileSummary, recommendations: recs };
}

// ---------------------------------------------------------------------------
// Typeform-compatible export
// ---------------------------------------------------------------------------

export function toTypeformFormat(): TypeformQuestion[] {
  return QUIZ_QUESTIONS.map((q) => ({
    ref: q.id,
    title: q.title,
    type: 'multiple_choice' as const,
    properties: {
      choices: q.choices.map((c) => ({ label: c.label, ref: c.id })),
      allow_multiple_selection: q.multiSelect ?? false,
    },
  }));
}
